/**
 * One clock for the whole tool.
 *
 * Growth timing shows up in four places — the renderer's phase durations, the
 * gap between arriving nodes, the demo's unfurl, and timeline replay — and if
 * any of them drifts from the others the choreography falls apart. So the base
 * durations live here and everything divides by the same user-chosen `tempo`.
 *
 * The defaults are deliberately unhurried. The point of watching an idea branch
 * is to follow the reasoning, and reasoning you can't read went past too fast.
 */
export const BASE = {
  /** Filopodia sample the possible directions before one is taken. */
  probe: 420,
  /** The growth cone crawls to its target. */
  extend: 1700,
  /** The shaft thickens behind a brightening wave. */
  consolidate: 1400,
  /** The touchdown flash. */
  arrive: 900,
  /**
   * Gap between one node arriving and the next starting.
   *
   * One value for live growth, the demo's opening, and timeline replay — they
   * are the same event and looked jarringly different when they were not.
   */
  beat: 780,
  /** One trip of the "working here" signal, seed → active node. */
  dispatch: 1600,
} as const

/** Total lifetime of a single strand's growth, before tempo. */
export const GROW_TOTAL_BASE = BASE.probe + BASE.extend + BASE.consolidate

/** Higher is faster. 1 is the considered default. */
export const TEMPOS = [
  { value: 0.35, label: '0.35×', name: 'Glacial' },
  { value: 0.5, label: '0.5×', name: 'Unhurried' },
  { value: 1, label: '1×', name: 'Considered' },
  { value: 1.75, label: '1.75×', name: 'Brisk' },
  { value: 3, label: '3×', name: 'Impatient' },
] as const

export const DEFAULT_TEMPO = 1

export function tempoName(t: number): string {
  return TEMPOS.find((x) => x.value === t)?.name ?? 'Custom'
}

/* ── The replay's pace ────────────────────────────────────────────────────
   A replay steps through `session.order` one arrival a beat, so its length is
   the graph's: the 129-claim exploration the study shared took about 100 s at
   1×, which is a film, not a glance. A copy opened from a link offers its
   replay as the first thing to do with it ("Replay how it grew (20 s)"), so a
   copy's replay is bounded instead — squeezed to fit, never stretched. */

/**
 * The longest a shared copy's replay runs, seed to finished graph.
 *
 * Twenty seconds: the design report's own figure for the press, and the length
 * of the worked example's opening unfurl (about 21 s, D122) — the growth this
 * app already asks a newcomer to sit through before anything else. A copy of up
 * to 26 claims replays at the ordinary beat, under it; a larger one is
 * compressed to it.
 */
export const COPY_REPLAY_MS = 20_000

/**
 * The shortest gap between two replay steps: the transport's floor since it had
 * one. Under it a step is not drawn apart from the next, so a replay that must
 * go faster takes several arrivals a step instead of a shorter step.
 */
export const REPLAY_STEP_FLOOR_MS = 60

export interface ReplayPace {
  /** What one arrival is worth on the transport's clock, at 1×: the beat, or
   *  the copy's share of its bound. The clock and the "(N s)" read this. */
  stepMs: number
  /** How often the playhead moves, at the chosen speed. */
  tickMs: number
  /** How many arrivals it moves each time — one, unless the floor forced more. */
  stride: number
}

/**
 * The replay's pace for a graph of `nodes` in `session.order`.
 *
 * `boundMs` null is the ordinary replay, and its arithmetic is the transport's
 * as it always was: one arrival per `BASE.beat / tempo / speed`, floored. With a
 * bound, the beat is shortened until the whole order fits it at 1×, and the
 * speed divides that. The playhead starts on the seed, so `nodes - 1` steps.
 */
export function replayPace(
  nodes: number,
  tempo: number,
  speed: number,
  boundMs: number | null,
): ReplayPace {
  const beat = BASE.beat / tempo
  const steps = Math.max(1, nodes - 1)
  const stepMs = boundMs !== null && steps * beat > boundMs ? boundMs / steps : beat
  const raw = stepMs / speed
  if (raw >= REPLAY_STEP_FLOOR_MS || boundMs === null) {
    return { stepMs, tickMs: Math.max(REPLAY_STEP_FLOOR_MS, raw), stride: 1 }
  }
  const stride = Math.ceil(REPLAY_STEP_FLOOR_MS / raw)
  return { stepMs, tickMs: raw * stride, stride }
}

/** How long a replay at 1× takes to reach the finished graph, in whole seconds
 *  and never under one: the "(N s)" on a copy's press. */
export function replaySeconds(nodes: number, tempo: number, boundMs: number | null): number {
  const { stepMs } = replayPace(nodes, tempo, 1, boundMs)
  return Math.max(1, Math.round((Math.max(1, nodes - 1) * stepMs) / 1000))
}
